import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import type { IHelpGuideSummary } from '@apis/help';
import type { IHelpSearchHit } from './searchGuides';
import { GuideIconTile } from './GuideIconTile';
import { EnglishOnlyPill } from './EnglishOnlyPill';
import { escapeRegExp, resultHref } from './helpFormat';
import styles from './HelpCenterPage.module.css';

interface IHighlightProps {
  text: string;
  query: string;
}

function Highlight({ text, query }: IHighlightProps) {
  const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'ig'));
  return <>{parts.map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part))}</>;
}

interface ISearchResultsProps {
  query: string;
  hits: IHelpSearchHit[];
  isError: boolean;
  bySlug: ReadonlyMap<string, IHelpGuideSummary>;
}

export function SearchResults({ query, hits, isError, bySlug }: ISearchResultsProps) {
  const { t } = useTranslation();
  if (isError) return <DataUnavailable message={t('help.loadFailed')} />;
  const shown = hits.filter((hit) => bySlug.has(hit.slug));
  return (
    <section>
      <div className={styles.sectionHead}>
        <h2>{t('help.resultsFor', { q: query })}</h2>
        <span>{t('help.matches', { count: shown.length })}</span>
      </div>
      {shown.length === 0 ? (
        <DataUnavailable message={t('help.noMatches', { q: query })} />
      ) : (
        <div className={styles.hits}>
          {shown.map((hit, i) => {
            const guide = bySlug.get(hit.slug)!;
            const parent = guide.parentSlug ? bySlug.get(guide.parentSlug) : undefined;
            return (
              <Link key={`${hit.slug}-${i}`} to={resultHref(hit, query)} className={styles.hit}>
                <GuideIconTile iconKey={guide.iconKey} variant="kid" />
                <span>
                  <span className={styles.where}>
                    {parent ? `${parent.title} › ` : ''}<b>{guide.title}</b>
                    {hit.headingText ? <> › <Highlight text={hit.headingText} query={query} /></> : null}
                  </span>
                  {hit.snippet ? <span className={styles.snippet}><Highlight text={hit.snippet} query={query} /></span> : null}
                </span>
                <EnglishOnlyPill language={guide.language} />
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
